import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { PlayButton } from "@/components/PlayButton";
import { ShareButton } from "@/components/ShareButton";
import { SpotifyButton } from "@/components/SpotifyButton";
import { ExpandableText } from "@/components/ExpandableText";
import { usePlayer } from "@/context/PlayerContext";
import { timeAgo, formatDate, imgUrl } from "@/lib/api";

export const mediaShape = (episode) => {
  if (episode.source === "tiktok") return { aspect: "aspect-[9/16]", hero: "md:grid-cols-[minmax(0,240px)_1fr] lg:grid-cols-[minmax(0,280px)_1fr]" };
  if (episode.video_id && !episode.audio_url) return { aspect: "aspect-video", hero: "md:grid-cols-[minmax(0,380px)_1fr] lg:grid-cols-[minmax(0,480px)_1fr]" };
  return { aspect: "aspect-square", hero: "md:grid-cols-[minmax(0,300px)_1fr] lg:grid-cols-[minmax(0,360px)_1fr]" };
};

export const EpisodeHero = ({ episode, eyebrow = "Newest on the network", showBadge = true, testId = "hero-episode", index = 0, linkTitle = true }) => {
  const { isPlaying } = usePlayer();
  const active = isPlaying(episode.id);
  const img = imgUrl(episode.image_url || episode.show?.cover_url);
  const shape = mediaShape(episode);
  const title = <h2 className={`${showBadge ? "mt-4" : ""} font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]`} data-testid={`${testId}-title`}>{episode.title}</h2>;
  return (
    <article className={`relative overflow-hidden rounded-3xl card-surface grain reveal ${active ? "now-playing" : ""}`} style={{ animationDelay: `${index * 50}ms` }} data-testid={`${testId}-card`} data-playing={active}>
      <div className="absolute inset-0 opacity-30 blur-3xl scale-125" style={{ backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className={`relative grid ${shape.hero} gap-8 p-6 sm:p-8 lg:p-10 items-center`}>
        <Link to={linkTitle ? `/episodes/${episode.id}` : `/shows/${episode.show?.slug}`} className={`block ${shape.aspect} rounded-2xl overflow-hidden shadow-2xl shadow-black/50 ring-1 ring-white/10`} data-testid={`${testId}-artwork`}>
          <img src={img} alt={episode.show?.name} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" loading={index ? "lazy" : "eager"} />
        </Link>
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-4">
            <span className="eyebrow">{eyebrow}</span>
            {active && <span className="bars flex items-end h-5" aria-hidden><span /><span /><span /><span /></span>}
          </div>
          {showBadge && <Link to={`/shows/${episode.show?.slug}`} className="inline-flex items-center gap-2 rounded-full bg-[#053671] px-3 py-1 text-xs font-bold tracking-wide text-white hover:bg-[#0B4A8F] transition-colors" data-testid={`${testId}-show-badge`}>{episode.show?.name}</Link>}
          {linkTitle ? <Link to={`/episodes/${episode.id}`} className="block hover:text-sky-300 transition-colors" data-testid={`${testId}-title-link`}>{title}</Link> : title}
          <ExpandableText text={episode.summary} full={episode.description} lines={3} className="mt-4 text-slate-300 max-w-2xl" testId={`${testId}-summary`} title={episode.title} eyebrow={episode.show?.name} />
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <PlayButton episode={episode} size="lg" testId={`${testId}-play-button`} />
            <SpotifyButton episode={episode} testId={`${testId}-spotify`} />
            <ShareButton episode={episode} testId={`${testId}-share`} />
            <div className="font-mono text-xs text-slate-400 flex items-center gap-4">
              <span title={formatDate(episode.published_at)}>{timeAgo(episode.published_at)}</span>
              {episode.duration && <span className="flex items-center gap-1"><Clock size={12} /> {episode.duration}</span>}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};
